// The NON-PC compiler's opportunity-attack packet uses the shared attack weapon selection.
// Missing inputs remain named; the browser owns both admission and the actual swing.
import { resolveAttackComposite } from '../actions/attack-weapon-resolution';
import { readInventoryField } from './inventory-raw-readers';
import { ABILITY_CHARGE_BUCKETS } from '../ability/charges/charge-entry.js';
import { storedStatusRows } from './creature-status-reader';
import { heldLayerlessDefinition } from './held-definition';

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => typeof v === 'string' ? v.toUpperCase() : '';

/** PHP empty(): null / '' / '0' / 0 / 0.0 / false / [] */
const phpEmpty = (v: unknown): boolean => v === undefined || v === null || v === '' || v === '0' || v === 0 || v === false
    || (Array.isArray(v) && v.length === 0) || (isObj(v) && Object.keys(v).length === 0);

export const OA_ATTACK_MODIFIER_KEYS = ['critical_hit_damage_multiplier', 'attacks_ignore_damage_mitigation', 'ignore_damage_type_resistance',
    'critical_hit_range_increase_mod_from_passives', 'opportunity_attack_roll_adv', 'off_hand_body_damage',
    'all_damage_output_changed_by_percentage', 'damage_type_reroll_1s_and_2s', 'damage_type_roll_1_becomes_2',
    'pack_tactics'];
const MANUFACTURED_KEYS = ['count_both_hands_weapon_equipped', 'count_main_hand_weapon_equipped', 'count_off_hand_weapon_equipped'];
/** the universal OA / Evade trio janus_trigger injects — skipped by the carrier walk */
const UNIVERSAL = new Set(['BE4CCA95-33F0-323D-BA54-844D2540C0F0', 'B2BC3219-C745-7050-51DD-892951F0026B', 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8']);
/** Outcome gates handled by the shared weapon fragment / on-hit executors. Incoming and unrelated listeners remain scoped out. */
const ON_HIT_KEYS = new Set(['triggers_on_hit', 'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit',
    'triggers_if_roll_type_is_at_adv', 'triggers_when_caster_makes_roll_type', 'triggers_when_caster_uses_item_type']);
const EXTRA_DAMAGE_ROLLS = new Set(['9878714A-27C3-D737-88B3-E8C1EE63A20F', '42A38751-5549-CDE7-528E-AED309A2B07D']);
/** janus_trigger_carriers() (janus_trigger_carriers.php) */
const TRIGGER_CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell', 'innate_spell_per_long_rest',
    'spells_known', 'opportunity_attack_ability'];
const PACKET_LAYERS = ['count_main_hand_natural_weapon_equipped', 'count_off_hand_natural_weapon_equipped',
    'equipped_main_hand_natural_weapon_item_id', 'equipped_off_hand_natural_weapon_item_id',
    'multiple_head_attacks', 'critical_hit_range_increase_mod', 'ignore_damage_type_resistance',
    'skill_rolls_adv', 'skill_rolls_dis', 'transform_killed_target_into_controlled_creature',
    'triggers_on_kill', 'triggers_on_opportunity_attack', 'triggers_on_unlucky_roll',
    'triggers_when_caster_gains_luck', 'triggers_when_caster_gains_status_effect',
    'triggers_when_target_gains_status_effect', 'immune_status_effect',
    'roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min', 'roll_type_roll_result_is_doubled', 'roll_type_roll_result_is_halved',
    'roll_type_against_is_rolled_at_max', 'roll_type_against_is_rolled_at_min', 'roll_type_against_roll_result_is_doubled', 'roll_type_against_roll_result_is_halved'];

export interface OpportunityInputDeps {
    itemField: (row: Row | null, key: string) => unknown;
    /** layer_value() over a row's item_layer_types (the compile's status read context) */
    layerValue: (row: Row, name: string) => unknown;
    /** layer_entries() over a row's item_layer_types */
    layerEntries: (row: Row, name: string) => unknown[];
    /** item_arr_scalar_layer_slot() */
    scalarSlot: (v: unknown) => unknown;
    /** a list entry's uuid (readLayerListEntryUuid) */
    entryUuid: (v: unknown) => string;
    /** query_full_item_arr(); null when the page does not hold it */
    definition: (id: string) => Row | null;
    /** calculate_equipped_counts() on a copy; throws / returns null when it cannot */
    equippedCounts: (row: Row) => Row | null;
}
export type OpportunityInput = { packet: Row } | { unknown: string };

/** PHP 8 is_numeric() for a string: optional surrounding whitespace, decimal / exponent forms only (no hex / binary / octal literals) */
const PHP_NUMERIC = /^[ \t\n\r\v\f]*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?[ \t\n\r\v\f]*$/;
const phpIsNumeric = (v: unknown): boolean => (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && PHP_NUMERIC.test(v));
/** PHP (int) cast; null when the result is not representable exactly (the caller then returns unknown) */
function phpInt(v: unknown): number | null {
    if (v === null || v === undefined || v === false) return 0;
    if (v === true) return 1;
    if (Array.isArray(v)) return v.length ? 1 : 0;
    if (isObj(v)) return Object.keys(v).length ? 1 : 0;
    let n: number;
    if (typeof v === 'number') n = v;
    else if (typeof v === 'string') { const m = /^[ \t\n\r\v\f]*([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?)/.exec(v); n = m ? Number(m[1]) : 0; }
    else return null;
    if (!Number.isFinite(n)) return null;
    const i = Math.trunc(n);
    return Number.isSafeInteger(i) ? i : null;
}
const no = (why: string): OpportunityInput => ({ packet: { schema: 'ft.opportunity-input.v1', supported: false, reason: why } });

/**
 * @param post  the NON-PC row AFTER the mutation: root keys as they will be served, item_layer_types WITH the root ability buckets,
 *              statuses / inventory / computed.creature post-mutation
 * @param attackModifierMoved  the compile could not prove the attack-modifier root keys unchanged (a provider set moved)
 */
export function opportunityAttackInput(post: Row, deps: OpportunityInputDeps, attackModifierMoved: boolean): OpportunityInput {
    const statuses = storedStatusRows(post, 'target');
    for (const status of statuses) {
        const embedded = isObj(status.status_arr) ? status.status_arr : null;
        const id = upper(deps.scalarSlot(status.status_item_id)) || upper(deps.entryUuid(embedded));
        const definition = heldLayerlessDefinition(embedded) ? embedded : deps.definition(id);
        if (!heldLayerlessDefinition(definition)) return no(`status-definition:${id || 'missing'}`);
    }
    if (!phpEmpty(post.triggers)) return no('trigger-settings');
    const has = (key: string): boolean => { const v = deps.layerValue(post, key); return v !== null && v !== undefined; };
    for (const key of OA_ATTACK_MODIFIER_KEYS) {
        if (!phpEmpty(post[key]) || has(key)) return no(key);
    }
    if (attackModifierMoved) return { unknown: 'attack-modifier-moved' };
    let manufactured = false;
    for (const key of MANUFACTURED_KEYS) {
        const n = phpInt(deps.layerValue(post, key) ?? 0); // (int) (layer_value(...) ?? 0)
        if (n === null) return { unknown: 'manufactured-cast' };
        manufactured ||= n > 0;
    }
    // creature_natural_weapon_entries(): the pristine layer + computed.creature.natural_weapon, deduped by scalar slot
    const weapons: unknown[] = [...deps.layerEntries(post, 'natural_weapon')];
    const derived = isObj(post.computed) && isObj((post.computed as Row).creature) ? ((post.computed as Row).creature as Row).natural_weapon : null;
    if (Array.isArray(derived) && derived.length) {
        const seen = new Set(weapons.map(e => String(deps.scalarSlot(e) ?? '')).filter(Boolean));
        for (const d of derived) { const uid = String(deps.scalarSlot(d) ?? ''); if (uid !== '' && seen.has(uid)) continue; if (uid !== '') seen.add(uid); weapons.push(d); }
    }
    if (!manufactured && weapons.length !== 1) return no('weapon-selection');
    if (!manufactured && deps.layerEntries(post, 'inventory').length) return no('inventory-loadout');
    const equippedActor: Row = { ...post, natural_weapon: weapons };
    const counted = deps.equippedCounts(equippedActor);
    if (!counted) return { unknown: 'equipped-counts' };
    // resolve_natural_weapon_id_for_attack_roll() over the ONE candidate: its id, unless the head-attack gate is live (in combat,
    // head_attack = 1, no multiple_head_attacks) and it sits in the head slot — combat state is not on the row: refuse that case
    let weaponId = upper(deps.entryUuid(weapons[0]));
    if (manufactured) {
        const selected = resolveAttackComposite({ ability: { items: { item_type: 'opportunity_attack_ability' } },
            caster: counted, weaponInventoryId: '' }, {
            value: (row, key) => row ? deps.layerValue(row, key) : null,
            entries: (row, key) => row ? deps.layerEntries(row, key) : [],
            itemField: deps.itemField, itemType: row => String(deps.itemField(row, 'item_type') ?? ''),
            inventoryField: readInventoryField, resolveItem: deps.definition,
        });
        if (!selected || 'unknown' in selected) return no(selected?.unknown ?? 'weapon-selection');
        weaponId = upper(readInventoryField(selected.composite, 'inventory_id') ?? deps.itemField(selected.composite, 'item_id'));
    }
    if (!/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(weaponId)) return no('weapon-selection');
    const headAttack = Number(deps.scalarSlot(isObj(post.encounter_creatures) ? (post.encounter_creatures as Row).head_attack : 0) ?? 0) === 1;
    if (!manufactured && headAttack && !has('multiple_head_attacks')) return { unknown: 'head-attack-gate' };
    if (!manufactured && !deps.definition(weaponId)) return { unknown: `def:${weaponId}` };
    const hookIds = new Set<string>();
    const chargeIds = new Set<string>();
    for (const carrier of TRIGGER_CARRIERS) {
        for (const entry of deps.layerEntries(post, carrier)) {
            const nested = isObj(entry) ? (isObj(entry.item_arr) ? entry.item_arr as Row : entry) : entry;
            const id = upper(typeof nested === 'string' ? nested : deps.entryUuid(entry));
            if (UNIVERSAL.has(id)) continue;
            let definition: Row | null = isObj(nested) && isObj(nested.item_layer_types) ? nested : null;
            if (!definition) {
                if (!/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)) return no('unknown-carrier');
                definition = deps.definition(id);
                if (!definition) return { unknown: `def:${id}` }; // PHP may hold what the page does not
            }
            const rt = deps.layerValue(definition, 'roll_type');
            if (typeof rt === 'object' && rt !== null) return { unknown: 'roll-type-shape' };
            const hitGated = ['triggers_on_hit', 'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit']
                .some(key => !phpEmpty(deps.layerValue(definition!, key)));
            const extra = EXTRA_DAMAGE_ROLLS.has(rt === null || rt === undefined ? '' : String(rt));
            if (hitGated || extra) {
                hookIds.add(id);
                for (const grant of deps.layerEntries(definition, 'add_charges_to_ability')) chargeIds.add(upper(deps.entryUuid(grant)));
            }
            for (const key of Object.keys(isObj(definition.item_layer_types) ? definition.item_layer_types as Row : {})) {
                if ((key.startsWith('triggers_') && (!(hitGated || extra) || !ON_HIT_KEYS.has(key))) || key === 'immune_status_effect') return no('authored-trigger');
            }
            const dv = (k: string): boolean => { const v = deps.layerValue(definition!, k); return v !== null && v !== undefined; };
            if (dv('damage_type_reroll_1s_and_2s') || dv('damage_type_roll_1_becomes_2')) return no('damage-reroll-provider');
        }
    }
    const layers: Row = {};
    // Unrelated passives were absent from MAIN's attack packet. Keep that projection while retaining each admitted hook's live pool.
    for (const carrier of new Set([...TRIGGER_CARRIERS, ...ABILITY_CHARGE_BUCKETS])) {
        const entries = [...deps.layerEntries(post, carrier)].filter(entry => {
            const id = upper(deps.entryUuid(entry));
            return hookIds.has(id) || chargeIds.has(id);
        });
        if (entries.length) layers[carrier] = structuredClone(entries);
    }
    for (const key of manufactured ? [...MANUFACTURED_KEYS, ...PACKET_LAYERS] : PACKET_LAYERS) {
        const value = key.startsWith('count_') || key.startsWith('equipped_') ? deps.scalarSlot(counted[key]) : deps.layerValue(post, key);
        if (value !== null && value !== undefined && value !== '') {
            if (typeof value === 'object') return no('complex-modifier');
            layers[key] = value;
        }
    }
    const computed = isObj(post.computed) && isObj((post.computed as Row).creature) ? (post.computed as Row).creature as Row : {};
    const reach = isObj(computed.reach) ? (computed.reach as Row).total : undefined;
    const numeric = phpIsNumeric;
    // PHP is_array(): a JSON list counts too
    const arrayish = (v: unknown): boolean => isObj(v) || Array.isArray(v);
    if (!arrayish(computed.skills) || !arrayish(computed.essences) || !numeric(reach)) return no('missing-computed-input');
    const rawSkill = isObj(computed.skills) ? (computed.skills as Row).natural_weapon_skill : undefined;
    // a non-array, non-null value would make PHP's array_intersect_key throw — not a packet the browser can predict
    if (rawSkill !== undefined && rawSkill !== null && typeof rawSkill !== 'object') return { unknown: 'skill-shape' };
    const skill = isObj(rawSkill) ? rawSkill : {};
    const naturalSkill: Row = {};
    for (const k of ['tier_level', 'adv_dis_stacks', 'skill_roll_mod']) if (k in skill) naturalSkill[k] = skill[k];
    const essences: Row = {};
    for (const key of ['body', 'fortune']) {
        const e = isObj(computed.essences) ? (computed.essences as Row)[key] : undefined;
        const total = isObj(e) ? e.total : undefined;
        if (!numeric(total)) return no('missing-essence');
        const n = phpInt(total);
        if (n === null) return { unknown: 'essence-cast' };
        essences[key] = { total: n };
    }
    const combat = isObj(computed.combat) ? computed.combat as Row : {};
    const extraAttack = phpInt(combat.extra_attack ?? 0);
    if (extraAttack === null) return { unknown: 'extra-attack-cast' };
    return { packet: { schema: 'ft.opportunity-input.v1', supported: true, weapon_id: weaponId,
        // lane RX S2 (2026-09-28): positional attack modifiers PROVEN absent on this watcher
        positional: { pack_tactics: false },
        // an empty PHP array encodes as a JSON LIST (item_layer_types, natural_weapon_skill)
        mechanics: { item_layer_types: Object.keys(layers).length ? layers : [], statuses: { target: [] },
            ...(manufactured ? { inventory: deps.layerEntries(post, 'inventory') } : {}), computed: { creature: {
            skills: manufactured ? computed.skills : { natural_weapon_skill: Object.keys(naturalSkill).length ? naturalSkill : [] }, essences,
            reach: { total: Number(reach) },
            combat: { extra_attack: extraAttack },
        } } } } };
}
